import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'

import { ErrorType } from '../../prop-types/error-prop-type'
import FilterableList from '../filterable-list/filterable-list'
import FormField from '../form/form-field'
import List from '../list/list'
import ListItem from '../list/list-item'
import { ErrorPopover } from '../popover/error-popover'
import DropdownHeader from './dropdown-header'

import styles from './dropdown.module.css'

const propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    error: ErrorType,
    filterPlaceholder: PropTypes.string,
    headerClassName: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    icon: PropTypes.object,
    itemKey: PropTypes.string,
    itemTitle: PropTypes.string,
    items: PropTypes.array.isRequired,
    onSelectionChanged: PropTypes.func,
    placeholder: PropTypes.string,
    placeholderSelectable: PropTypes.bool,
    selected: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    showFilter: PropTypes.bool,
    style: PropTypes.object // eslint-disable-line react/forbid-prop-types
}
const defaultProps = {
    itemKey: 'id',
    itemTitle: 'name'
}

/**
 * A dropdown component with optional inline text filter to filter (reduce) the dropdown items. A placholder may be
 * set as selectable item.
 *
 * Usage:
 *
 * @deprecated Please use <Select/>
 *
 * @example
 * <Dropdown />
 *
 * @property {array} items: PropTypes.array.isRequired - Items to use for selection.
 * @property {object} [icon]: PropTypes.object - Icon instance to show on the left.
 * @property {object} [selected]: PropTypes.object - Preselected item, must be in the items.
 * @property {function} [onSelectionChanged]: PropTypes.func - Callback when selection changes. Passes list of selected
 *                                                             items.
 * @property {string} [placeholder]: PropTypes.string - Placeholder text to show when nothing is selected grouped
 *                                                      together into a TagGroup. This is the title of the group.
 * @property {boolean} [placeholderSelectable]: PropTypes.bool - Placeholder is a selectable entry in dropdown to clear
 *                                                               selection from items.
 * @property {boolean} [showFilter]: PropTypes.bool - Flag to show the filter box in the dropdown.
 * @property {string} [filterPlaceholder]: PropTypes.string - Placeholder text for the filter textfield.
 * @property {string} [itemTitle]: PropTypes.string - Item property name to filter by and to show the value as item
 *                                                    label.
 * @property {string} [itemKey]: PropTypes.string - Item key property to identify the items.
 * @property {boolean} [disabled]: PropTypes.bool - Flag to disable the input, read only.
 *
 */

export default class Dropdown extends FormField {
    constructor(props) {
        super(props)
        const { itemKey, itemTitle } = this.props
        // Virtual item representing the placeholder which can be prepended to the item list to be selectable
        this.virtualPlaceholderItem = { [itemKey]: NaN, [itemTitle]: this.props.placeholder }
        const unselectedItem = this.props.placeholderSelectable ? this.virtualPlaceholderItem : undefined
        this.state = {
            dropdownOpen: false,
            selected: this.props.selected ? this.props.selected : unselectedItem
        }
        // We are not using bind(this) here, because we had strange behaviour in the past.
        // It might be related to the fact, that we subclass this component in TagDropdown.
        // TODO: bind(this) on subclassed components needs further testing and investigation.
        this.onDropdown = this.onDropdown.bind(this)
    }

    componentDidUpdate(prevProps) {
        const { placeholder } = this.props
        if (placeholder !== prevProps.placeholder) {
            this.virtualPlaceholderItem = {
                [this.props.itemKey]: NaN,
                [this.props.itemTitle]: placeholder
            }
        }
    }

    UNSAFE_componentWillMount() {
        document.addEventListener('click', this.onDropdown, false)
    }

    componentWillUnmount() {
        // Do not bind here, because otherwise the function instance in componentWillMount and
        // componentWillUnmount is not the same and the listener is not removed.
        document.removeEventListener('click', this.onDropdown, false)
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({ selected: nextProps.selected })
    }

    render() {
        const { className, error, style } = this.props
        const composedClassName = cx(
            'tag',
            styles['dropdown'],
            {
                [styles['dropdown-open']]: this.state.dropdownOpen,
                [styles['error']]: error
            },
            className
        )

        return (
            <ErrorPopover error={error} placement="top">
                <div className={composedClassName} style={style}>
                    {this.renderHeader()}
                    {this.renderList()}
                </div>
            </ErrorPopover>
        )
    }

    renderItem(item) {
        const { itemKey, itemTitle, showFilter } = this.props
        const selected = this.isItemSelected(item)
        const className = cx('t3sel-dropdown-list-item', styles['list-item'], {
            [styles['list-item-selected']]: selected
        })
        const ListItem = this.getListItemClass()

        if (showFilter) {
            return (
                <ListItem
                    key={item[itemKey]}
                    className={className}
                    selected={selected}
                    onClick={() => {
                        this.onChoiceClick.bind(this)(item)
                    }}
                    name={item[itemTitle]}
                />
            )
        }
        return (
            <ListItem
                key={item[itemKey]}
                className={className}
                selected={selected}
                onClick={() => {
                    this.onChoiceClick.bind(this)(item)
                }}
            >
                {item[itemTitle]}
            </ListItem>
        )
    }

    renderHeader() {
        const { itemTitle } = this.props
        const selectedItemName = this.state.selected ? this.state.selected[itemTitle] : null
        return <DropdownHeader {...this.props} item={selectedItemName} />
    }

    renderList() {
        const { filterPlaceholder, placeholderSelectable, showFilter } = this.props
        const listClassName = cx('t3sel-dropdown-list', styles['choices-list'])
        const className = cx(styles['list'], {
            [styles['list-open']]: this.state.dropdownOpen
        })
        const items = placeholderSelectable ? [this.virtualPlaceholderItem, ...this.props.items] : this.props.items
        let content

        if (showFilter) {
            // The filterBy attribute here is quite confusing as it refers to the name attribute in the <ListItem>
            // child within <FilterableList> and the filter compares the value of that attribute
            content = (
                <FilterableList
                    ref={ref => (this.filterableListRef = ref)}
                    placeholder={filterPlaceholder}
                    filterBy="name"
                    className={styles['filterable-list']}
                    listClassName={listClassName}
                >
                    {items.map(this.renderItem.bind(this))}
                </FilterableList>
            )
        } else {
            content = <List className={listClassName}>{items.map(this.renderItem.bind(this))}</List>
        }
        return (
            <div ref={ref => (this.listRef = ref)} className={className}>
                {content}
            </div>
        )
    }

    isItemSelected(item) {
        const { itemKey } = this.props
        return this.state.selected === item || (!this.state.selected && !item[itemKey])
    }

    getValue() {
        return this.state.selected
    }

    getListItemClass() {
        // We need this to easily change the list items in the subclassed components
        // without overriding the whole renderItem() method
        return ListItem
    }

    onSelectionChanged(item) {
        if (this.props.onSelectionChanged) {
            this.props.onSelectionChanged(item)
        }
    }

    onDropdown(event) {
        const { dropdownOpen } = this.state
        const { disabled } = this.props
        let newState = dropdownOpen

        // Do not toggle
        if (!disabled && this.listRef && !this.listRef.contains(event.target)) {
            newState = ReactDOM.findDOMNode(this).contains(event.target) ? !dropdownOpen : false
        }

        if (this.props.showFilter && this.filterableListRef && !newState) {
            this.filterableListRef.reset.bind(this.filterableListRef)()
        }

        this.setState({ dropdownOpen: newState })
    }

    onChoiceClick(item) {
        this.setState({ dropdownOpen: false, selected: item })
        this.onSelectionChanged(item === this.virtualPlaceholderItem ? undefined : item)
    }
}

Dropdown.propTypes = propTypes
Dropdown.defaultProps = defaultProps
